import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { isNil } from 'lodash-es';

import { getErrorMessage } from '@/utils/errors';
import type { RootState } from '../index';

/**
 * The owner's notifications: the ones Prisma (the partner's CRM) sends, shown in the top bar's bell. A new one is
 * announced by the bell (it rings, its dot pops in), never in a pop-up. Owner Hub keeps them
 * (docs/owner-notifications.md there):
 *   GET  {userApiUrl}/notifications          the newest 30, the unread count, the newest not announced (`popup`)
 *   POST {userApiUrl}/notifications/shown    { id }: the bell announced it; it and older ones count as shown
 *   POST {userApiUrl}/notifications/read     { id } or { all: true }
 * A new one also arrives as `owner_notification` on the owner-hub websocket (store/ws/ws.js), so an owner who is
 * online hears of it at once. Owner-scoped, so it is cleared on session teardown.
 */

export type OwnerNotification = {
  id: string;
  title: string;
  body: string;
  /** An https address or an Owner Studio path ("/wallet"); '' for none. */
  link_url: string;
  link_label: string;
  created_at: string | null;
  shown_at: string | null;
  read_at: string | null;
};

type ListResponse = { notifications: OwnerNotification[]; unread: number; popup: OwnerNotification | null; unshown: number };

export type OwnerNotificationsState = ListResponse & {
  loaded: boolean;
  /** The last read failed (Owner Hub unreachable, or without the inbox yet). */
  failed: boolean;
  /** Bumped by every local change (an arrival, shown, read): a read started before the latest change is stale. */
  changes: number;
  /** The newest read in flight and the `changes` it started at. Only its answer lands, and only if nothing changed
   *  locally since: after a change a fresh read follows, so the state always settles on Owner Hub's. */
  fetching: { requestId: string; changes: number } | null;
  /** The last one this tab announced (the bell rang and marked it shown). If Owner Hub refused the mark, its list
   *  still names it as the one to announce: not again here, or the bell rings and marks it over and over. */
  announced: string | null;
};

const LIST_LIMIT = 30;

const initialState: OwnerNotificationsState = {
  notifications: [],
  unread: 0,
  popup: null,
  unshown: 0,
  loaded: false,
  failed: false,
  changes: 0,
  fetching: null,
  announced: null,
};

const apiUrl = (): string => `${window.config.userApiUrl}/notifications`;

const authHeaders = (state: RootState): Record<string, string> | null => {
  const token = state.authentication.access_token;
  return isNil(token) ? null : { Authorization: token };
};

type GenericError = { rejectValue: { error: string } };

export const fetchOwnerNotifications = createAsyncThunk<ListResponse, void, GenericError>(
  'ownerNotifications/fetch',
  async (_, { getState, rejectWithValue }) => {
    const headers = authHeaders(getState() as RootState);
    if (!headers) return rejectWithValue({ error: 'No authentication token found' });
    try {
      const { data } = await axios.get<ListResponse>(apiUrl(), { headers });
      return data;
    } catch (error: unknown) {
      return rejectWithValue({ error: getErrorMessage(error) });
    }
  },
);

/** The bell announced notification `id`: it and the ones waiting from before it count as shown; one made after it
 *  is still announced. Owner Hub's list is read again after, so a refused write does not linger. */
export const markOwnerNotificationsShown = createAsyncThunk<void, string, GenericError>(
  'ownerNotifications/shown',
  async (id, { getState, dispatch, rejectWithValue }) => {
    const headers = authHeaders(getState() as RootState);
    if (!headers) return rejectWithValue({ error: 'No authentication token found' });
    try {
      await axios.post(`${apiUrl()}/shown`, { id }, { headers });
      return undefined;
    } catch (error: unknown) {
      return rejectWithValue({ error: getErrorMessage(error) });
    } finally {
      void dispatch(fetchOwnerNotifications());
    }
  },
);

/** One notification read (its id), or all of them (no id). Owner Hub's list is read again after, as for shown. */
export const markOwnerNotificationRead = createAsyncThunk<void, string | undefined, GenericError>(
  'ownerNotifications/read',
  async (id, { getState, dispatch, rejectWithValue }) => {
    const headers = authHeaders(getState() as RootState);
    if (!headers) return rejectWithValue({ error: 'No authentication token found' });
    try {
      await axios.post(`${apiUrl()}/read`, id ? { id } : { all: true }, { headers });
      return undefined;
    } catch (error: unknown) {
      return rejectWithValue({ error: getErrorMessage(error) });
    } finally {
      void dispatch(fetchOwnerNotifications());
    }
  },
);

const ownerNotificationsSlice = createSlice({
  name: 'ownerNotifications',
  initialState,
  reducers: {
    /** A new notification pushed on the websocket: first in the bell, and announced by it, unless an admin is
     *  impersonating the owner or the tab is not in view (`announce: false`; the announcement is the owner's). */
    ownerNotificationArrived: (state, action: PayloadAction<{ notification: OwnerNotification; announce: boolean }>) => {
      const n = action.payload.notification;
      if (!n?.id || state.notifications.some((x) => x.id === n.id)) return;
      state.changes += 1;
      state.notifications = [n, ...state.notifications].slice(0, LIST_LIMIT);
      if (!n.read_at) state.unread += 1;
      if (action.payload.announce && !n.shown_at) {
        state.popup = n;
        state.unshown += 1;
      }
    },
    resetOwnerNotifications: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchOwnerNotifications.pending, (state, action) => {
      state.fetching = { requestId: action.meta.requestId, changes: state.changes };
    });
    builder.addCase(fetchOwnerNotifications.fulfilled, (state, action) => {
      // An older read, or one started before a local change, would bring stale marks (or an announcement) back.
      if (state.fetching?.requestId !== action.meta.requestId) return state;
      if (state.fetching.changes !== state.changes) return { ...state, fetching: null };
      const popup = action.payload.popup?.id === state.announced ? null : action.payload.popup;
      return { ...action.payload, popup, loaded: true, failed: false, changes: state.changes, fetching: null, announced: state.announced };
    });
    builder.addCase(fetchOwnerNotifications.rejected, (state, action) => {
      if (state.fetching?.requestId !== action.meta.requestId) return;
      state.fetching = null;
      state.loaded = true;
      state.failed = true;
    });
    builder.addCase(markOwnerNotificationsShown.pending, (state, action) => {
      state.changes += 1;
      state.announced = action.meta.arg;
      const now = new Date().toISOString();
      const shown = state.notifications.find((n) => n.id === action.meta.arg) ?? (state.popup?.id === action.meta.arg ? state.popup : null);
      const upTo = shown?.created_at ?? null;
      // the one shown and those from before it; a later one (it arrived meanwhile) is still announced
      state.notifications = state.notifications.map((n) =>
        n.shown_at || (upTo && n.created_at && n.created_at > upTo) ? n : { ...n, shown_at: now },
      );
      if (state.popup?.id === action.meta.arg) {
        state.popup = null;
        state.unshown = 0;
      }
    });
    builder.addCase(markOwnerNotificationRead.pending, (state, action) => {
      const id = action.meta.arg;
      state.changes += 1;
      // Only a notification still unread here counts down: reading one twice (two clicks, two tabs) is one.
      const wasUnread = id ? state.notifications.some((n) => n.id === id && !n.read_at) : false;
      const now = new Date().toISOString();
      state.notifications = state.notifications.map((n) =>
        (id ? n.id === id : true) && !n.read_at ? { ...n, read_at: now, shown_at: n.shown_at ?? now } : n,
      );
      state.unread = id ? Math.max(0, state.unread - (wasUnread ? 1 : 0)) : 0;
      if (!id || state.popup?.id === id) state.popup = null;
    });
  },
});

export const { ownerNotificationArrived, resetOwnerNotifications } = ownerNotificationsSlice.actions;

export const selectOwnerNotifications = (state: RootState): OwnerNotificationsState => state.ownerNotifications;

export default ownerNotificationsSlice.reducer;
